import { useEffect } from "react";

/**
 * @typedef {Object} ScrollTrackerConfig
 * @property {boolean} [enabled] - Enable scroll tracking
 * @property {boolean} [debug] - Enable debug logging
 * @property {string} [storageKey] - LocalStorage key for enabling tracking
 */
const defaultConfig = {
  enabled: false, // (process.env.NODE_ENV === "development" || localStorage.getItem("@sudowrite-dev/enable-scroll-listeners") === "true") && localStorage.getItem("@sudowrite-dev/force-disable-scroll-listeners") !== "true",
  debug: false,
};

/**
 * Hook for tracking and debugging scroll events. Used only in development, but can be enabled in production if need be.
 * Remember that this CAN be dangerous, since we're overriding the prototype of Element.
 * @param {ScrollTrackerConfig} [config] - Configuration options
 */
const useScrollTracker = (config = defaultConfig) => {
  useEffect(() => {
    if (typeof window === "undefined") return;

    if (!config.enabled) {
      console.info("📜 Scroll tracking disabled");
      return;
    }

    console.info("📜 Scroll tracking initialised");

    const original = {
      scroll: window.scroll,
      scrollTo: window.scrollTo,
      scrollBy: window.scrollBy,
      scrollIntoView: Element.prototype.scrollIntoView,
    };

    const getStackTrace = () => {
      const err = new Error();
      Error.captureStackTrace(err);
      return err.stack || "";
    };

    const logScrollEvent = (method, args) => {
      console.group(`📜 Scroll Event: ${method}`);
      console.info("Arguments:", args);
      console.info("Stack Trace:", getStackTrace());
      console.groupEnd();
    };

    const methods = ["scroll", "scrollTo", "scrollBy"];
    methods.forEach((method) => {
      window[method] = function (...args) {
        logScrollEvent(`window.${method}`, args);
        return original[method].apply(this, args);
      };
    });

    Element.prototype.scrollIntoView = function (...args) {
      logScrollEvent("element.scrollIntoView", args);
      return original.scrollIntoView.apply(this, args);
    };

    return () => {
      methods.forEach((method) => {
        window[method] = original[method];
      });
      Element.prototype.scrollIntoView = original.scrollIntoView;
    };
  }, [config.enabled, config.debug, config.storageKey]);
};

export default useScrollTracker;
